'use client';

import { useOrganizationList, useUser } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import { useOrgPeople } from '../../hooks/use-org-people';
import { Link } from '../../i18n/navigation';
import { capitalise } from '../../lib/capitalise';
import { getOrgDisplayName } from '../../lib/org-brand';
import { isValidOrgSlug, PERSONAL_SLUG } from '../../libs/auth/org-slug';
import { orgScopeQuery } from '../../libs/workspace/org-scope';
import { OrgAvatar } from '../OrgAvatar';

interface PickerEntry {
  readonly slug: string;
  readonly displayName: string;
  readonly avatarName: string;
  readonly imageUrl: string | null;
  readonly isPat: boolean;
  readonly memberCount: number;
}

const OTHER_INTEGRATIONS = ['stripe', 'linear', 'datadog'] as const;

const DISCONNECTED_CLASS =
  'bg-violet-400/10 text-violet-700 line-through dark:bg-violet-400/10 dark:text-violet-300';

function readGithubLogin(metadata: unknown): string | null {
  if (metadata === null || typeof metadata !== 'object') return null;
  const value = (metadata as Record<string, unknown>)['githubLogin'];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function Checkmark(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      className="h-2.5 w-2.5"
      aria-hidden="true"
    >
      <path
        d="M2.5 6.5L5 9L9.5 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IntegrationBadges({
  isPat,
  t,
}: {
  readonly isPat: boolean;
  readonly t: (key: string) => string;
}): React.JSX.Element {
  const githubLabel = isPat ? t('orgScope.pat') : t('orgPickerGitHubApp');
  const githubClass = isPat
    ? 'bg-amber-400/15 text-amber-200 dark:bg-amber-400/15 dark:text-amber-200'
    : 'bg-sky-400/15 text-sky-300 dark:bg-sky-400/15 dark:text-sky-300';

  return (
    <div className="flex flex-wrap justify-center gap-1">
      <span
        className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${githubClass}`}
      >
        {githubLabel}
      </span>
      {OTHER_INTEGRATIONS.map((id) => (
        <span
          key={id}
          className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${DISCONNECTED_CLASS}`}
        >
          {t(`orgPickerInt.${id}`)}
        </span>
      ))}
    </div>
  );
}

function OrgCard({
  entry,
  checked,
  onToggle,
  t,
}: {
  readonly entry: PickerEntry;
  readonly checked: boolean;
  readonly onToggle: (slug: string) => void;
  readonly t: (key: string) => string;
}): React.JSX.Element {
  return (
    <label
      className={`relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 px-4 pb-4 pt-5 text-center transition-all focus-within:ring-2 focus-within:ring-purple-400 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-950 ${
        checked
          ? 'border-purple-500 bg-purple-900 shadow-sm dark:border-purple-500 dark:bg-purple-900'
          : 'border-purple-900 bg-purple-950 shadow-sm hover:border-purple-600 hover:shadow-md dark:border-purple-900 dark:bg-[#2e1065] dark:hover:border-purple-600'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        aria-label={entry.displayName}
        onChange={() => {
          onToggle(entry.slug);
        }}
        className="sr-only"
      />
      <div
        className={`absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded ${
          checked
            ? 'bg-purple-500 text-white dark:bg-purple-400'
            : 'border-2 border-purple-500 dark:border-purple-500'
        }`}
      >
        {checked ? <Checkmark /> : null}
      </div>
      <OrgAvatar
        orgName={entry.avatarName}
        imageUrl={entry.imageUrl}
        size="md"
      />
      <span className="max-w-full truncate text-sm font-bold text-purple-100 dark:text-purple-100">
        {entry.displayName}
      </span>
      <IntegrationBadges isPat={entry.isPat} t={t} />
      <div className="flex items-center justify-center gap-1.5">
        {entry.memberCount > 0 ? (
          <span className="text-xs text-purple-400 dark:text-purple-300">
            <span className="font-bold text-purple-200 dark:text-purple-100">
              {entry.memberCount}
            </span>{' '}
            {t('orgPickerMembers')}
          </span>
        ) : null}
      </div>
      {entry.isPat ? (
        <span className="rounded bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-200 dark:bg-amber-400/10 dark:text-amber-200">
          {t('orgPickerSetupHint')}
        </span>
      ) : (
        <span className="rounded bg-emerald-400/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-300 dark:bg-emerald-400/10 dark:text-emerald-300">
          {t('orgPickerConnected')}
        </span>
      )}
    </label>
  );
}

export function OrgPicker(): React.JSX.Element {
  const t = useTranslations('workspace');
  const { user } = useUser();
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 100 },
  });
  const directory = useOrgPeople(null);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const toggle = useCallback((slug: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const toggleAll = useCallback((allSlugs: readonly string[]) => {
    setSelected((previous) =>
      previous.size === allSlugs.length ? new Set() : new Set(allSlugs),
    );
  }, []);

  const membersByOrg = useMemo(() => {
    const map = new Map<string, number>();
    for (const member of directory.members) {
      for (const org of member.orgs) {
        map.set(org.toLowerCase(), (map.get(org.toLowerCase()) ?? 0) + 1);
      }
    }
    return map;
  }, [directory.members]);

  if (!(isLoaded && user)) {
    return <p className="text-muted-foreground">{t('loading')}</p>;
  }

  const clerkEntries: readonly PickerEntry[] = userMemberships.data
    .map((membership): PickerEntry | null => {
      const login = readGithubLogin(membership.organization.publicMetadata);
      if (
        login === null ||
        !isValidOrgSlug(login) ||
        login.toLowerCase() === PERSONAL_SLUG
      ) {
        return null;
      }
      const slug = login.toLowerCase();
      return {
        slug,
        displayName: getOrgDisplayName(login) ?? capitalise(login),
        avatarName: login,
        imageUrl: membership.organization.imageUrl,
        isPat: false,
        memberCount: membersByOrg.get(slug) ?? 0,
      };
    })
    .filter((entry): entry is PickerEntry => entry !== null);

  const clerkSlugs = new Set(clerkEntries.map((entry) => entry.slug));
  const patEntries: readonly PickerEntry[] = directory.orgs
    .filter(
      (login) =>
        login.toLowerCase() !== PERSONAL_SLUG &&
        !clerkSlugs.has(login.toLowerCase()),
    )
    .map((login) => {
      const slug = login.toLowerCase();
      return {
        slug,
        displayName: capitalise(login),
        avatarName: login,
        imageUrl: `https://github.com/${login}.png`,
        isPat: true,
        memberCount: membersByOrg.get(slug) ?? 0,
      };
    });

  const orgEntries = [...clerkEntries, ...patEntries];
  const allSlugs = orgEntries.map((entry) => entry.slug);
  const continueHref = `/work-items${orgScopeQuery([...selected])}`;
  const continueContent = (
    <>
      {t('orgPickerContinue')}
      <span className="rounded bg-white/25 px-2 py-0.5 text-xs font-bold tabular-nums">
        {selected.size} / {orgEntries.length}
      </span>
    </>
  );

  return (
    <section className="mx-auto w-full max-w-2xl">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {orgEntries.map((entry) => (
          <OrgCard
            key={entry.slug}
            entry={entry}
            checked={selected.has(entry.slug)}
            onToggle={toggle}
            t={t}
          />
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {directory.loaded ? (
          <Link
            href={continueHref}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
          >
            {continueContent}
          </Link>
        ) : (
          <span
            aria-disabled={true}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-300 px-6 py-2.5 text-sm font-semibold text-purple-100 dark:bg-purple-800 dark:text-purple-500"
          >
            {continueContent}
          </span>
        )}
        <button
          type="button"
          onClick={() => {
            toggleAll(allSlugs);
          }}
          className="inline-flex items-center rounded-lg border border-purple-300 bg-transparent px-6 py-2.5 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-950/30"
        >
          {selected.size > 0 && selected.size === orgEntries.length
            ? t('orgPickerDeselectAll')
            : t('orgPickerSelectAll')}
        </button>
      </div>
      <p className="mt-5 text-center text-xs leading-snug text-muted-foreground">
        {t('orgPickerIntNote')}
      </p>
    </section>
  );
}
